import { useStockLevels, unitLevelKey } from '../../store/stockLevels';
import { useTenantConfig } from '../../store/tenantConfig';
import { useLang } from '../../lib/language';

/**
 * The on-hand chip on a cart line.
 *
 *   ● 12 in stock            — plenty
 *   ● Not enough (2 left)    — more asked than on hand, and the tenant BLOCKS
 *                              negative stock: the sale will be refused
 *   ● Out of stock           — nothing on hand, same tenant
 *
 * A tenant that ALLOWS selling below stock gets no warning at all — no chip on
 * a short or empty line (asked 2026-09-23: the amber "Only 2 left" was noise
 * for a shop that sells past zero on purpose). It still sees "N in stock"
 * while there is enough.
 *
 * `requested` is the total across every line of this item IN THE SAME UNIT, not
 * this line's qty, so two lines of the same item and unit both light up
 * together.
 *
 * Both numbers must be in the SAME unit or the chip is nonsense: the balance is
 * read from `unitLevels` for this line's own unit (a carton line is sized
 * against cartons), and `requested` is summed per (item, unit) by the caller.
 * Mixing them made a 6-carton line read "120 pcs in stock" and never warn,
 * while the server still refused the sale — chip and refusal disagreeing on one
 * cart. Falls back to the per-item level only when the API sent no per-unit row.
 */
export function StockChip(props: {
  itemId: number;
  unitId?: number | null;
  requested: number;
  isReturn?: boolean;
}): JSX.Element | null {
  const unitLevel = useStockLevels((s) => s.unitLevels[unitLevelKey(props.itemId, props.unitId)]);
  const itemLevel = useStockLevels((s) => s.levels[props.itemId]);
  const level = unitLevel ?? itemLevel;
  const allowNegative = useTenantConfig((s) => s.allowNegativeStock);
  const { t, lang } = useLang();
  if (!level) return null;

  const unit = (lang === 'ar' ? level.unitNameAr : level.unitName) || level.unitName || '';
  const have = level.onHand;
  const qty = fmtQty(have);
  const withUnit = unit ? `${qty} ${unit}` : qty;

  // A return line only informs; nothing is taken off the shelf.
  if (props.isReturn) {
    return (
      <span className="stock-chip stock-chip--info" title={t({ en: 'On hand at your store', ar: 'الرصيد في مخزنك' })}>
        {t({ en: `${withUnit} in stock`, ar: `${withUnit} بالمخزن` })}
      </span>
    );
  }

  // Selling below stock is allowed: nothing to warn about, so no chip at all
  // on a short or empty line.
  if (allowNegative && (have <= 0 || props.requested > have)) return null;
  if (have <= 0) {
    return (
      <span className="stock-chip stock-chip--block">
        {t({ en: 'Out of stock', ar: 'غير متوفر' })}
        {have < 0 && ` (${withUnit})`}
      </span>
    );
  }
  if (props.requested > have) {
    return (
      <span className="stock-chip stock-chip--block">
        {t({ en: `Not enough — ${withUnit} left`, ar: `غير كافٍ — المتبقي ${withUnit}` })}
      </span>
    );
  }
  return (
    <span className="stock-chip stock-chip--ok" title={t({ en: 'On hand at your store', ar: 'الرصيد في مخزنك' })}>
      {t({ en: `${withUnit} in stock`, ar: `${withUnit} بالمخزن` })}
    </span>
  );
}

function fmtQty(n: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 3 });
}
